import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import testIds from '@smart-tv/e2e/shared/variables';

import Backdrop from '@smart-tv/components/backdrop/backdrop';
import Button from '@smart-tv/components/button/button';
import Typography from '@smart-tv/components/typography/typography';

import useCookieConsent from '@smart-tv/hooks/use-cookie-consent/use-cookie-consent';

import CookieBannerDialog from './cookie-banner-dialog';

import styles from './cookie-banner.module.scss';

const HEADER_ID = 'cookie-banner-header';
const TEXT_ID = 'cookie-banner-text';

export const CookieBanner = () => {
  const [openCookieBannerDialog, setOpenCookieBannerDialog] = useState(false);
  const bannerRef = useRef<HTMLElement>(null);
  const showCookiesButtonRef = useRef<HTMLButtonElement>(null);

  const { handleAcceptCookies, handleRejectCookies } = useCookieConsent();

  const handleOpenCookieBannerDialog = useCallback(() => {
    setOpenCookieBannerDialog(true);
  }, []);

  const handleCloseCookieBannerDialog = useCallback(() => {
    setOpenCookieBannerDialog(false);
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (root) root.inert = true;
    bannerRef.current?.focus();

    return () => {
      if (root) root.inert = false;
      previouslyFocused?.focus();
    };
  }, []);

  useEffect(() => {
    const banner = bannerRef.current;
    const showCookiesButton = showCookiesButtonRef.current;

    if (!openCookieBannerDialog || !banner) return;

    banner.inert = true;

    return () => {
      banner.inert = false;
      showCookiesButton?.focus();
    };
  }, [openCookieBannerDialog]);

  return createPortal(
    <>
      <Backdrop open={true} className={styles['cookie-banner__backdrop']} />

      <section
        ref={bannerRef}
        className={styles['cookie-banner']}
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADER_ID}
        aria-describedby={TEXT_ID}
        tabIndex={-1}
      >
        <Typography id={HEADER_ID} variant="h5" className={styles['cookie-banner__header']}>
          Denne hjemmeside bruger cookies
        </Typography>

        <Typography id={TEXT_ID} variant="bodySmall" className={styles['cookie-banner__text']}>
          Norlys bruger cookies til at tilpasse vores indhold og annoncer, til at vise dig
          funktioner til sociale medier og til at analysere vores trafik. Vi deler også oplysninger
          om din brug af vores website med vores partnere inden for sociale medier,
          annonceringspartnere og analysepartnere. Vores partnere kan kombinere disse data med andre
          oplysninger, du har givet dem, eller som de har indsamlet fra din brug af deres tjenester.
          Du samtykker til vores cookies, hvis du fortsætter med at anvende vores hjemmeside.
        </Typography>

        <Typography variant="bodySmall" className={styles['cookie-banner__links']}>
          <a
            className={styles['cookie-banner__link']}
            href="https://norlys.dk/cookiepolitik/"
            rel="noreferrer"
            target="_blank"
            data-testid={testIds.cookieBanner.cookiePolicy}
          >
            Læs mere om vores cookiepolitik
          </a>
          &nbsp;|&nbsp;
          <button
            ref={showCookiesButtonRef}
            type="button"
            className={styles['cookie-banner__link']}
            onClick={handleOpenCookieBannerDialog}
            data-testid={testIds.cookieBanner.showCookies}
          >
            Vis cookies
          </button>
        </Typography>

        <div className={styles['cookie-banner__buttons']}>
          <Button onClick={handleAcceptCookies} data-testid={testIds.cookieBanner.accept}>
            Accepter
          </Button>

          <Button onClick={handleRejectCookies} data-testid={testIds.cookieBanner.reject}>
            Afvis
          </Button>
        </div>
      </section>

      <CookieBannerDialog open={openCookieBannerDialog} onClose={handleCloseCookieBannerDialog} />
    </>,
    document.body
  );
};

export default memo(CookieBanner);
